import React, { useState } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  Divider,
  CircularProgress,
  Stack,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import BookIcon from "@mui/icons-material/Book";
import CloseIcon from "@mui/icons-material/Close";
import { enqueueSnackbar } from "notistack";
import { Icons } from "../../../assets/icon";

interface PaymentForKaraokeRequestProps {
  open: boolean;
  onClose: () => void;
  flashCardId: number;
  requestType: "song" | "story";
  requestText: string;
  requestPrice: number;
  purchaseChip?: number;
  earnedChip?: number;
  onConfirm: () => void;
}

const PaymentForKaraokeRequest: React.FC<PaymentForKaraokeRequestProps> = ({
  open,
  onClose,
  flashCardId,
  requestType,
  requestText,
  requestPrice,
  earnedChip = 0,
  purchaseChip = 0,
  onConfirm,
}) => {
  const [loading, setLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleConfirmClick = () => {
    if (purchaseChip < requestPrice) {
      enqueueSnackbar("Insufficient chips. Please purchase more chips.", {
        variant: "error",
      });
      return;
    }
    setShowConfirmation(true);
  };

  const handleConfirmProceed = async () => {
    setLoading(true);
    try {
      await onConfirm();
      setShowConfirmation(false);
      onClose();
    } catch (error: any) {
      console.error("Request submission failed:", error);
      enqueueSnackbar(
        error?.response?.data?.message || "Failed to submit request",
        { variant: "error" },
      );
    } finally {
      setLoading(false);
    }
  };

  const requestTypeLabel =
    requestType === "song" ? "Song Request" : "Story Text";
  const requestIcon = requestType === "song" ? <MusicNoteIcon /> : <BookIcon />;

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Box
          sx={{
            p: 4,
            backgroundColor: "#1A1A2E",
            width: { xs: "90%", sm: 380 },
            mx: "auto",
            mt: "15vh",
            borderRadius: 3,
            boxShadow: "0 24px 48px rgba(0,0,0,0.5)",
            position: "relative",
            border: "1px solid rgba(255,255,255,0.1)",
            outline: "none",
          }}
        >
          <IconButton
            onClick={onClose}
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              color: "#FFFFFF",
            }}
          >
            <CloseIcon />
          </IconButton>

          <Stack direction="row" spacing={1} alignItems="center" mb={1}>
            <Box sx={{ color: "#E50914" }}>{requestIcon}</Box>
            <Typography variant="h6" fontWeight={700} sx={{ color: "#FFFFFF" }}>
              {requestTypeLabel}
            </Typography>
          </Stack>

          <Typography
            variant="body2"
            sx={{ color: "rgba(255,255,255,0.6)", mb: 2 }}
          >
            Submit your{" "}
            {requestType === "song" ? "song request" : "story text"} to the
            talent
          </Typography>

          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              backgroundColor: "rgba(255,255,255,0.05)",
              mb: 2,
              border: "1px solid rgba(255,255,255,0.1)",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: "rgba(255,255,255,0.5)",
                mb: 0.5,
                fontSize: 12,
              }}
            >
              {requestType === "song" ? "Song Name:" : "Story Text:"}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                color: "#FFFFFF",
                wordBreak: "break-word",
              }}
            >
              {requestText}
            </Typography>
          </Box>

          <Typography
            variant="body2"
            sx={{ color: "rgba(255,255,255,0.6)", mb: 1 }}
          >
            Cost:{" "}
            <strong style={{ color: "#E50914" }}>
              <Icons.Coin /> {requestPrice}
            </strong>
          </Typography>

          <Typography
            variant="body2"
            sx={{ color: "rgba(255,255,255,0.6)", mb: 1 }}
          >
            Purchase Chips:{" "}
            <strong style={{ color: "#E50914" }}>
              <Icons.Coin /> {purchaseChip}
            </strong>
          </Typography>

          <Typography
            variant="body2"
            sx={{ color: "rgba(255,255,255,0.6)", mb: 2 }}
          >
            Earned Chips:{" "}
            <strong style={{ color: "#E50914" }}>
              <Icons.Coin /> {earnedChip}
            </strong>
          </Typography>

          {purchaseChip < requestPrice && (
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                backgroundColor: "rgba(229,9,20,0.15)",
                border: "1px solid rgba(229,9,20,0.3)",
                mb: 2,
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: "#E50914", fontSize: 13 }}
              >
                Insufficient chips. You need {requestPrice - purchaseChip} more
                chips to submit this request.
              </Typography>
            </Box>
          )}

          <Divider sx={{ my: 2, borderColor: "rgba(255,255,255,0.1)" }} />

          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button
              variant="outlined"
              onClick={onClose}
              disabled={loading}
              sx={{
                fontWeight: 600,
                px: 3,
                py: 1,
                borderRadius: 2,
                textTransform: "none",
                borderColor: "rgba(255,255,255,0.25)",
                color: "#FFFFFF",
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,0.05)",
                  borderColor: "rgba(255,255,255,0.4)",
                },
              }}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              onClick={handleConfirmClick}
              disabled={loading || purchaseChip < requestPrice}
              sx={{
                fontWeight: 600,
                px: 3,
                py: 1,
                borderRadius: 2,
                textTransform: "none",
                backgroundColor: "#E50914",
                "&:hover": { backgroundColor: "#C1121F" },
                "&:disabled": {
                  backgroundColor: "rgba(255,255,255,0.1)",
                  color: "rgba(255,255,255,0.3)",
                },
              }}
            >
              {loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Proceed"
              )}
            </Button>
          </Stack>
        </Box>
      </Modal>

      {/* Confirmation Dialog */}
      <Dialog
        open={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "#1A1A2E",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 3,
            boxShadow: "0 24px 48px rgba(0,0,0,0.5)",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, color: "#FFFFFF" }}>
          Confirm {requestTypeLabel}
        </DialogTitle>
        <DialogContent>
          <Typography
            variant="body1"
            sx={{ mb: 2, color: "rgba(255,255,255,0.8)" }}
          >
            Are you sure you want to spend{" "}
            <strong style={{ color: "#E50914" }}>
              <Icons.Coin /> {requestPrice}
            </strong>{" "}
            to submit this{" "}
            {requestType === "song" ? "song request" : "story text"}?
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "rgba(255,255,255,0.5)" }}
          >
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0, gap: 1 }}>
          <Button
            onClick={() => setShowConfirmation(false)}
            variant="outlined"
            sx={{
              fontWeight: 600,
              textTransform: "none",
              borderRadius: 2,
              borderColor: "rgba(255,255,255,0.25)",
              color: "#FFFFFF",
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.05)",
                borderColor: "rgba(255,255,255,0.4)",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmProceed}
            variant="contained"
            disabled={loading}
            sx={{
              fontWeight: 600,
              textTransform: "none",
              borderRadius: 2,
              backgroundColor: "#E50914",
              "&:hover": { backgroundColor: "#C1121F" },
            }}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Confirm & Submit"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PaymentForKaraokeRequest;
